import { UnreachableCaseError } from 'lib/utils';

import type { DelegatedKeyAuthorization } from './authorizationReducer';
import type { DelegatedKeySettings } from './settingsReducer';

const REDUX_DELEGATED_CACHE_ID = 'katana_dk_v4';

/** Note: This cannot be easily changed cause users might have it cached */
export type StorageDelegatedAuthorization = DelegatedKeyAuthorization;

/** Note: This cannot be easily changed cause users might have it cached */
export type StorageDelegatedSettings = DelegatedKeySettings;

const getStorage = (engine: DelegatedKeySettings['engine']) =>
  engine === 'persistent' ? window.localStorage : window.sessionStorage;

export function loadStoredDelegatedAuthorization(
  delegatedSettings?: DelegatedKeySettings
): StorageDelegatedAuthorization | undefined {
  const delegatedEngine = delegatedSettings?.engine;
  if (!delegatedEngine) {
    return;
  }
  const serializedDelegatedState = getStorage(delegatedEngine).getItem(
    REDUX_DELEGATED_CACHE_ID
  );
  try {
    const authorization = serializedDelegatedState
      ? JSON.parse(serializedDelegatedState)
      : undefined;
    if (checkIfParsedIsDelegatedKey(authorization)) {
      return authorization;
    }
  } catch (error) {
    console.warn(error);
  }
}

let lastStored: string | undefined;

export function storeDelegatedAuthorization(
  delegatedSettings: DelegatedKeySettings,
  delegatedAuthorization?: StorageDelegatedAuthorization
) {
  try {
    const authorizationSerialized = JSON.stringify(delegatedAuthorization);
    if (lastStored === authorizationSerialized) {
      // No changes to state which should be stored
      return;
    }
    lastStored = authorizationSerialized;

    const { engine } = delegatedSettings;
    if (!engine) {
      console.debug('DK storage engine unknown');
      getStorage('session').removeItem(REDUX_DELEGATED_CACHE_ID);
      getStorage('persistent').removeItem(REDUX_DELEGATED_CACHE_ID);
      return;
    }
    switch (engine) {
      case 'persistent':
        getStorage('session').removeItem(REDUX_DELEGATED_CACHE_ID);
        break;
      case 'session':
        getStorage('persistent').removeItem(REDUX_DELEGATED_CACHE_ID);
        break;
      default:
        throw new UnreachableCaseError(engine, 'Not handled engine');
    }
    getStorage(engine).setItem(
      REDUX_DELEGATED_CACHE_ID,
      authorizationSerialized
    );
  } catch (error) {
    console.warn(error);
  }
}

function checkIfParsedIsDelegatedKey(
  parsed?: StorageDelegatedAuthorization
): parsed is StorageDelegatedAuthorization {
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return false;
  }
  return (
    typeof parsed.createdTime === 'number' &&
    parsed.walletType === 'delegatedKey' &&
    typeof parsed.walletAddress === 'string' &&
    typeof parsed.delegatedKeyEncrypted === 'string'
  );
}
